/**
 * Privacy-first analytics shim — Umami-compatible, cookieless.
 *
 * Behavior:
 * - Respects `navigator.doNotTrack`: no script load, no events sent.
 * - Sets zero cookies (Umami is cookieless by design).
 * - If no website ID is configured, becomes a safe no-op so dev/preview
 *   builds don't error.
 *
 * Configure with Vite env vars:
 *   VITE_UMAMI_WEBSITE_ID, VITE_UMAMI_SCRIPT_URL
 */

const UMAMI_WEBSITE_ID =
  (import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined) ?? '';
const UMAMI_SCRIPT_URL =
  (import.meta.env.VITE_UMAMI_SCRIPT_URL as string | undefined) ??
  'https://cloud.umami.is/script.js';

type UmamiTrack = (
  eventOrPayload?: string | Record<string, unknown> | ((props: Record<string, unknown>) => Record<string, unknown>),
  data?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    umami?: { track: UmamiTrack };
  }
}

let scriptLoaded = false;
let scriptReady = false;

type QueuedCall =
  | { kind: 'pageview'; path: string }
  | { kind: 'event'; name: string; data?: Record<string, unknown> };

/**
 * Calls made before the Umami script finishes loading are buffered here
 * and replayed once `window.umami.track` is available. This lets us
 * defer script injection until after LCP without losing the first
 * pageview or any early conversion events.
 */
const callQueue: QueuedCall[] = [];

const flushQueue = (): void => {
  if (!scriptReady || typeof window === 'undefined' || !window.umami) return;
  while (callQueue.length > 0) {
    const c = callQueue.shift()!;
    try {
      if (c.kind === 'pageview') {
        window.umami.track((props) => ({ ...props, url: c.path }));
      } else {
        window.umami.track(c.name, c.data);
      }
    } catch {
      /* never throw from analytics */
    }
  }
};

function dntEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { doNotTrack?: string; msDoNotTrack?: string };
  const win = typeof window !== 'undefined' ? (window as Window & { doNotTrack?: string }) : undefined;
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack ?? win?.doNotTrack;
  return dnt === '1' || dnt === 'yes';
}

function isEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!UMAMI_WEBSITE_ID) return false;
  if (dntEnabled()) return false;
  return true;
}

/**
 * Inject the Umami script tag. Marked private — callers should go
 * through `initAnalytics()` so the load is deferred until after LCP.
 */
function injectScript(): void {
  if (scriptLoaded || !isEnabled()) return;
  scriptLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = UMAMI_SCRIPT_URL;
  script.setAttribute('data-website-id', UMAMI_WEBSITE_ID);
  script.setAttribute('data-auto-track', 'false');
  script.setAttribute('data-do-not-track', 'true');
  // Once the script loads, mark ready and replay queued calls. Umami
  // exposes `window.umami` synchronously when the script executes.
  script.addEventListener('load', () => {
    scriptReady = true;
    flushQueue();
  });
  script.addEventListener('error', () => {
    // Drop the queue silently — analytics must never block the app.
    callQueue.length = 0;
  });
  document.head.appendChild(script);
}

/**
 * Defer Umami injection until after LCP so the analytics request never
 * competes with the hero asset for the main thread or socket pool.
 * Strategy:
 *   1. Subscribe to `largest-contentful-paint` via PerformanceObserver.
 *      The Umami script is injected on the first LCP entry (browsers
 *      fire one per candidate; we disconnect after the first).
 *   2. Always also schedule a fallback `requestIdleCallback` (with a
 *      4 s timeout) so we still load on browsers without LCP support
 *      (Safari < 16) or pages that never produce an LCP entry.
 *   3. Final safety net: a `pagehide`/`load` listener guarantees the
 *      script is requested before the user navigates away.
 * All paths are guarded by `scriptLoaded` so injection happens once.
 */
export function initAnalytics(): void {
  if (scriptLoaded || !isEnabled()) return;
  if (typeof window === 'undefined') return;

  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    injectScript();
  };

  // 1. After-LCP path
  try {
    if ('PerformanceObserver' in window) {
      const supported = (PerformanceObserver as unknown as {
        supportedEntryTypes?: string[];
      }).supportedEntryTypes;
      if (!supported || supported.includes('largest-contentful-paint')) {
        const po = new PerformanceObserver(() => {
          po.disconnect();
          // Wait one idle tick after LCP so we don't fight the paint
          // commit for the same frame.
          schedulePostLcp(fire);
        });
        po.observe({ type: 'largest-contentful-paint', buffered: true });
      }
    }
  } catch {
    /* PO not available; fall through to fallbacks */
  }

  // 2. Idle-time fallback (caps the wait so we still load on no-LCP pages)
  schedulePostLcp(fire, 4000);

  // 3. Last-chance safety net: load before unload so we never miss the
  // first pageview on a bounce.
  const onLeave = () => fire();
  window.addEventListener('pagehide', onLeave, { once: true });
  window.addEventListener('beforeunload', onLeave, { once: true });
}

const schedulePostLcp = (cb: () => void, timeout = 2000): void => {
  type IdleWindow = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(cb, { timeout });
  } else {
    setTimeout(cb, Math.min(timeout, 1500));
  }
};

/** Track a SPA pageview for the given path. */
export function trackPageview(path: string): void {
  if (!isEnabled() || typeof window === 'undefined') return;
  try {
    if (window.umami && scriptReady) {
      window.umami.track((props) => ({ ...props, url: path }));
    } else {
      callQueue.push({ kind: 'pageview', path });
    }
  } catch {
    // never throw from analytics
  }
}

/** Track a named custom event with optional properties. */
export function trackEvent(name: string, data?: Record<string, unknown>): void {
  if (!isEnabled() || typeof window === 'undefined') return;
  try {
    if (window.umami && scriptReady) {
      window.umami.track(name, data);
    } else {
      callQueue.push({ kind: 'event', name, data });
    }
  } catch {
    // never throw from analytics
  }
}

export const trackContactFormSubmit = (): void => {
  trackEvent('contact_form_submit');
};

export const trackDocumentSearch = (query: string, resultCount: number): void => {
  trackEvent('document_search', { query, resultCount });
};

export const trackExternalCtaClick = (destination: string): void => {
  trackEvent('external_cta_click', { destination });
};

/**
 * Reports how long a route's Suspense fallback was visible. A duration of
 * 0 means the lazy chunk was already cached (likely thanks to prefetch);
 * higher numbers indicate the user actually saw the skeleton.
 */
export const trackRouteFallback = (
  path: string,
  durationMs: number,
  prefetched: boolean,
  cacheHit: boolean = false,
): void => {
  trackEvent('route_fallback', {
    path,
    durationMs: Math.round(durationMs),
    prefetched,
    cacheHit,
  });
};

/**
 * Consolidated per-navigation timing breakdown. Emitted once per route
 * change after the destination route has rendered.
 *
 * - totalMs: navigation start -> render complete
 * - startToSuspenseMs: time spent before/around Suspense (effectively the
 *   non-blocked render time; equals totalMs when no fallback was shown)
 * - suspenseMs: cumulative time a Suspense fallback was visible (0 when
 *   the lazy chunk was already cached, e.g. via prefetch)
 */
export const trackRouteTiming = (payload: {
  path: string;
  totalMs: number;
  startToSuspenseMs: number;
  suspenseMs: number;
  prefetched: boolean;
  cacheHit: boolean;
}): void => {
  trackEvent('route_timing', {
    path: payload.path,
    totalMs: Math.round(payload.totalMs),
    startToSuspenseMs: Math.round(payload.startToSuspenseMs),
    suspenseMs: Math.round(payload.suspenseMs),
    prefetched: payload.prefetched,
    cacheHit: payload.cacheHit,
  });
};
